import * as d3 from 'd3'

import { ObjectHash, Theme } from '../../types/shared'
import Text from '../canvas/text'
import tailwindColors from '../constants/colors'
import { INDICATOR_LABEL_HEIGHT, IndicatorType, TextAlign, TextBaseline } from '../constants/common'
import utils from '../utils'
import { drawInVisibleArea } from '../utils/draw_in_visible_area'
import Indicator from './indicator'

export interface ICOT {
  code: string
  name: string
  units: string
  commercialHedgers: number[]
  largeTraders: number[]
  smallTraders: number[]
}

interface Attrs {
  period: string
  code: string
}

type ColorsKeys = keyof Pick<ICOT, 'commercialHedgers' | 'largeTraders' | 'smallTraders'>

class COT extends Indicator<Attrs> {
  static type = IndicatorType.Cot
  static label = 'COT'

  static getNumOfBarsBuffer() {
    return 0
  }

  declare colors: ObjectHash<string, ColorsKeys>

  compute() {
    if (!this.isComputeNecessary() || !this.data.COTs) return
    const cotQuoteCode = Object.keys(this.data.COTs)[0]
    if (!this.attrs.code && cotQuoteCode) {
      this.attrs.code = cotQuoteCode
    }
    const cot = this.data.COTs[this.attrs.code]
    const [min, max] = utils.minMax(cot.commercialHedgers, cot.largeTraders, cot.smallTraders)
    this.max = Math.max(Math.abs(max), Math.abs(min))
    this.min = -this.max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    if (!this.data.COTs) return
    context.translate(0.5, 0.5)
    for (const key in this.colors) {
      context.set('strokeStyle', this.colors[key as ColorsKeys])
      context.beginPath()
      const data = this.data.COTs[this.attrs.code][key as ColorsKeys]
      drawInVisibleArea({
        quote: this.data,
        scale: this.model.scale,
        leftOffset: this.leftOffset,
        width: this.width,
        drawBarCallback: (i: number, x: number) => {
          context.lineTo(x, Math.round(this.fy(data[i])))
        },
      })
      context.stroke()
    }
    context.translate(-0.5, -0.5)
  }

  renderLabel(context: CanvasRenderingContext2D) {
    if (!this.data.COTs) return
    const { ChartSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general
    const name = new Text(
      {
        text: this.toString(),
        x: 8,
        y: 16,
        font: { size: 10, style: 'bold' },
        fillStyle: Colors.text,
        textAlign: TextAlign.left,
        textBaseline: TextBaseline.bottom,
      },
      this.model
    )
    name.render(context)

    new Text(
      {
        text: this.data.COTs[this.attrs.code].units as string,
        x: 38 + name.measure(),
        y: 16,
        font: { size: 8 },
        fillStyle: Colors.text,
        textAlign: TextAlign.left,
        textBaseline: TextBaseline.bottom,
      },
      this.model
    ).render(context)

    this.renderAdditionalLabels(context)
  }

  renderLabelChartsPage(context: CanvasRenderingContext2D) {
    const { x, y, labelWidth } = super.renderLabelChartsPage(context) as { x: number; y: number; labelWidth: number }

    const { IndicatorSettings } = this.getChartLayoutSettings()
    const isDarkMode = this.model.chart().chart_layout().theme === Theme.dark
    const secondarylabelX = labelWidth + x * 2
    const fontSettings = IndicatorSettings.left.indicatorLabel.font
    const scaleCoefficient = 0.7

    const secondarylabel = new Text(
      {
        text: this.data.COTs[this.attrs.code].units as string,
        x: secondarylabelX + x,
        font: Text.getMergedPropsWithDefaults('font', {
          ...fontSettings,
          size: fontSettings.size ? fontSettings.size * scaleCoefficient : undefined,
          style: 'normal',
        }),
        fillStyle: tailwindColors.gray[isDarkMode ? 50 : 900],
        textAlign: TextAlign.left,
        textBaseline: TextBaseline.middle,
      },
      this.model
    )
    secondarylabel.set({
      y: y + (INDICATOR_LABEL_HEIGHT * scaleCoefficient - (secondarylabel.attrs.lineHeight * scaleCoefficient) / 2),
    })
    secondarylabel.render(context)

    this.renderAdditionalLabels(context)
  }

  renderAdditionalLabels(context: CanvasRenderingContext2D) {
    const { ChartSettings, IndicatorSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general

    new Text(
      {
        text: 'COT',
        x: 23,
        y: IndicatorSettings.top.height + (IndicatorSettings.bottom.height + this.contentHeight) / 2,
        angle: -90,
        font: { size: 10, weight: '900', family: 'Lato, sans-serif' },
        fillStyle: Colors.textSecondary,
        textAlign: TextAlign.center,
        textBaseline: TextBaseline.alphabetic,
      },
      this.model
    ).render(context)

    const text = new Text(
      {
        text: 'Commercial Hedgers',
        x: IndicatorSettings.left.width,
        y: IndicatorSettings.top.height + this.contentHeight + IndicatorSettings.bottom.height - 1, // allign to very bottom of indicator with 1 pixel bottom padding
        font: { size: 8, style: 'bold' },
        fillStyle: this.colors.commercialHedgers,
        textAlign: TextAlign.left,
        textBaseline: TextBaseline.bottom,
      },
      this.model
    )
    text.render(context)

    let x = text.get('x')! + 8 + text.measure()
    text.set({ text: 'Large Traders', x, fillStyle: this.colors.largeTraders })
    text.render(context)

    x += 8 + text.measure()
    text.set({ text: 'Small Traders', x, fillStyle: this.colors.smallTraders })
    text.render(context)
  }

  formatAxis(value: number) {
    // https://github.com/d3/d3-format/blob/master/README.md#locale_formatPrefix
    const scaleY = this.model.scale.y as unknown as d3.ScaleLinear<number, number>
    return scaleY.tickFormat(4, 's')(value).replace(/G/, 'B')
  }

  getModalConfig() {
    return {
      title: 'COT',
      inputs: [],
      inputsErrorMessages: {},
    }
  }

  toString() {
    return this.data?.COTs ? (this.data.COTs[this.attrs.code].name as string) : undefined
  }

  toConfig<T extends Indicator>(): T['attrs'] & { type: T['type'] } {
    return {
      type: this.type,
      code: this.attrs.code,
    }
  }
}

COT.prototype.colors = {
  commercialHedgers: '#10cc10',
  largeTraders: '#cc1010',
  smallTraders: '#1010cc',
}

export default COT
